import React, { useCallback } from 'react';
import { MutationFn, MutationOptions } from 'react-apollo';
import { core_apimessages_Error as Error } from './rql';
import { parseGraphqlErrors } from './parse_graphql_errors';
import { ExecutionResult } from 'graphql';

export interface MutationResult<MutationData> {
  data: MutationData;
  errors: Error[];
  loading: boolean;
}

export type MutationFunction<MutationData, Variables> =
  (variables: MutationOptions<MutationData, Variables>) => Promise<ExecutionResult<MutationData>>;

type MutationTuple<MutationData, Variables> = [
  MutationFunction<MutationData, Variables>,
  MutationResult<MutationData>,
];

/**
 * Port of [Apollo useMutation](https://www.apollographql.com/docs/react/data/mutations)
 * hook. Takes a mutation provided by a higher-order component graphql-tag mutation
 * and provides the fetch result, state and wrapped Core Errors as a result.
 *
 * @example
 * const [addToCart, { data, loading, errors }] = useUpdateOrderQuantity(props.editCart);
 *
 * <button onClick={addToCart}>Add to Cart</button>
 */
export function useMutation<MutationData, Variables>(
  mutation: MutationFn<MutationData, Variables>,
): MutationTuple<MutationData, Variables> {
  const [result, setResult] = React.useState<MutationResult<MutationData>>({
    data: null,
    errors: [],
    loading: false,
  });

  const execute = useCallback(async (variables: MutationOptions<MutationData, Variables>) => {
    setResult({
      data: null,
      errors: [],
      loading: true,
    });

    try {
      const fetchResult = await mutation(variables);
      if (fetchResult) {
        const successResult = {
          data: fetchResult.data,
          errors: [],
          loading: false,
        };
        setResult(successResult);

        return successResult;
      }

      const nonFatalResult = {
        data: null,
        errors: [],
        loading: false,
      };
      setResult(nonFatalResult);

      return nonFatalResult;
    } catch (e) {
      const errors = parseGraphqlErrors(e);
      const errorResult = {
        data: null,
        errors,
        loading: false,
      };
      setResult(errorResult);

      return errorResult;
    }
  }, [mutation]);

  return [execute, result];
}
